import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, IconButton, Tooltip, Alert, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControlLabel, Checkbox, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { taskTemplatesAPI } from '../../services/api';
import { TASK_TYPES, DISCIPLINES } from '../../pages/technician/taskConstants';

const EMPTY = {
  title: '', description: '', discipline: '', task_type: '', estimated_hours: '',
  per_equipment: false, safety_notes: '', checklist: '', project_type: '', order: 0,
};

/**
 * Plantillas que alimentan el boton "Generar tareas" de las ordenes de
 * fabricacion. Si no hay ninguna configurada se aplica el paquete de serie.
 */
export default function TaskTemplates() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await taskTemplatesAPI.list();
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se han podido cargar las plantillas.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY);
    setDialog(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      ...EMPTY,
      ...t,
      estimated_hours: t.estimated_hours ?? '',
      checklist: (t.checklist || []).join('\n'),
      description: t.description || '',
      discipline: t.discipline || '',
      task_type: t.task_type || '',
      safety_notes: t.safety_notes || '',
      project_type: t.project_type || '',
    });
    setDialog(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        estimated_hours: form.estimated_hours === '' ? null : parseFloat(form.estimated_hours),
        checklist: form.checklist.split('\n').map((s) => s.trim()).filter(Boolean),
      };
      if (editId) await taskTemplatesAPI.update(editId, payload);
      else await taskTemplatesAPI.create(payload);
      setDialog(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido guardar.');
    } finally {
      setBusy(false);
    }
  };

  const loadDefaults = async () => {
    setBusy(true);
    try {
      await taskTemplatesAPI.loadDefaults();
      await load();
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se han podido cargar las de serie.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await taskTemplatesAPI.delete(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido eliminar.');
    }
  };

  if (!data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  const rows = data.using_defaults
    ? data.defaults.map((d, i) => ({ ...d, id: `def-${i}`, readonly: true }))
    : data.templates;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
          Estas plantillas son las que usa el boton <strong>"Generar tareas"</strong> de cada orden
          de fabricacion. Las marcadas <em>por maquina</em> crean una tarea por cada equipo de la orden.
        </Typography>
        {data.using_defaults ? (
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={loadDefaults}
            disabled={busy}>
            Editar las de serie
          </Button>
        ) : (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Nueva plantilla
          </Button>
        )}
      </Box>

      {data.using_defaults && (
        <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
          No hay plantillas propias, asi que se aplica el paquete de serie que ves abajo.
          Pulsa <strong>"Editar las de serie"</strong> para copiarlas y poder modificarlas.
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#c62828' }}>
              {['Tarea', 'Disciplina', 'Tipo', 'Horas', 'Por maquina', 'Checklist']
                .map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 'bold', color: '#fff' }}>{h}</TableCell>
                ))}
              <TableCell align="right" sx={{ width: 90, color: '#fff' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell>{t.title}</TableCell>
                <TableCell>{t.discipline || '—'}</TableCell>
                <TableCell>{t.task_type || '—'}</TableCell>
                <TableCell>{t.estimated_hours ?? '—'}</TableCell>
                <TableCell>
                  {t.per_equipment
                    ? <Chip size="small" color="primary" label="Si" />
                    : <Typography variant="body2" color="textSecondary">No</Typography>}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="textSecondary">
                    {(t.checklist || []).length} puntos
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {!t.readonly && (
                    <>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error"
                          onClick={() => setConfirmDelete(t)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Editar plantilla' : 'Nueva plantilla'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label="Titulo de la tarea" value={form.title} required
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField fullWidth multiline rows={2} label="Descripcion" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Stack direction="row" spacing={2}>
              <TextField select fullWidth label="Disciplina" value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}>
                <MenuItem value="">Sin definir</MenuItem>
                {DISCIPLINES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
              <TextField select fullWidth label="Tipo" value={form.task_type}
                onChange={(e) => setForm({ ...form, task_type: e.target.value })}>
                <MenuItem value="">Sin definir</MenuItem>
                {TASK_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField fullWidth label="Horas estimadas" value={form.estimated_hours}
                onChange={(e) => setForm({ ...form, estimated_hours: e.target.value.replace(',', '.') })}
                inputProps={{ inputMode: 'decimal' }} />
              <TextField fullWidth label="Orden" value={form.order} type="number"
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })} />
            </Stack>
            <FormControlLabel
              control={<Checkbox checked={!!form.per_equipment}
                onChange={(e) => setForm({ ...form, per_equipment: e.target.checked })} />}
              label="Crear una tarea por cada maquina de la orden"
            />
            <TextField fullWidth multiline rows={4} label="Checklist (un punto por linea)"
              value={form.checklist}
              onChange={(e) => setForm({ ...form, checklist: e.target.value })} />
            <TextField fullWidth multiline rows={2} label="Notas de seguridad"
              value={form.safety_notes}
              onChange={(e) => setForm({ ...form, safety_notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={busy || !form.title.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Eliminar plantilla</DialogTitle>
        <DialogContent>
          <Typography>¿Eliminar "{confirmDelete?.title}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={remove}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
